import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';
import toast from 'react-hot-toast';
import { Sparkles, Check, X, ArrowRightLeft, Clock } from 'lucide-react';

interface ConversionRequest {
 id: string;
 requestId: string;
 userId: string;
 franchiseId: string;
 branchId?: string;
 stampsRequested: number;
 pointsCost: number;
 status: 'pending' | 'approved' | 'rejected';
 createdAt: any;
 approvedAt?: any;
 approvedBy?: string;
 rejectedAt?: any;
 rejectedBy?: string;
 rejectionReason?: string;
}

interface UserData {
 displayName: string;
 email: string;
 queuePoints: number;
}

interface Props {
 selectedFranchiseId: string;
 selectedBranchId: string;
}

export const PointsManagementPanel: React.FC<Props> = ({ selectedFranchiseId, selectedBranchId }) => {
 const { t } = useTranslation();
 const [pendingRequests, setPendingRequests] = useState<ConversionRequest[]>([]);
 const [processedRequests, setProcessedRequests] = useState<ConversionRequest[]>([]);
 const [loading, setLoading] = useState(true);
 const [processingId, setProcessingId] = useState<string | null>(null);
 const [userDataCache, setUserDataCache] = useState<Record<string, UserData>>({});
 const [showProcessed, setShowProcessed] = useState(false);
 const [rejectModalOpen, setRejectModalOpen] = useState(false);
 const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
 const [rejectionReason, setRejectionReason] = useState('');

 useEffect(() => {
 // Subscribe to pending conversion requests
 // Note: Conversion requests are franchise-level, not branch-level
 // Clients belong to franchises, not specific branches
 const queryConstraints: any[] = [];

 // Add where clauses first (Firestore requirement)
 if (selectedFranchiseId !== 'all') {
 queryConstraints.push(where('franchiseId', '==', selectedFranchiseId));
 }
 queryConstraints.push(where('status', '==', 'pending'));

 // Add orderBy last
 queryConstraints.push(orderBy('createdAt', 'desc'));

 // Don't filter by branchId - clients belong to franchise, not branch

 const requestsQuery = query(
 collection(db, 'conversion_requests'),
 ...queryConstraints
 );

 const unsubPending = onSnapshot(
 requestsQuery,
 async (snapshot) => {
 const requestsData = snapshot.docs.map(doc => ({
 id: doc.id,
 requestId: doc.id,
 ...doc.data()
 })) as ConversionRequest[];

 setPendingRequests(requestsData);
 setLoading(false);

 // Load user data for each request
 const userIds = [...new Set(requestsData.map(r => r.userId))];
 for (const userId of userIds) {
 if (!userDataCache[userId]) {
 loadUserData(userId);
 }
 }
 },
 (error) => {
 console.error('Error loading conversion requests:', error);
 setLoading(false);
 toast.error(t('admin.points.errorLoadingRequests'));
 }
 );

 return () => unsubPending();
 }, [selectedFranchiseId, selectedBranchId]);

 // Load processed requests when toggled
 useEffect(() => {
 if (!showProcessed) return;

 const queryConstraints: any[] = [];

 // Add where clauses first (Firestore requirement)
 if (selectedFranchiseId !== 'all') {
 queryConstraints.push(where('franchiseId', '==', selectedFranchiseId));
 }
 queryConstraints.push(where('status', 'in', ['approved', 'rejected']));

 // Add orderBy last
 queryConstraints.push(orderBy('createdAt', 'desc'));

 // Don't filter by branchId - clients belong to franchise, not branch

 const processedQuery = query(
 collection(db, 'conversion_requests'),
 ...queryConstraints
 );

 const unsubProcessed = onSnapshot(
 processedQuery,
 async (snapshot) => {
 const requestsData = snapshot.docs.map(doc => ({
 id: doc.id,
 requestId: doc.id,
 ...doc.data()
 })) as ConversionRequest[];

 setProcessedRequests(requestsData);

 // Load user data for each request
 const userIds = [...new Set(requestsData.map(r => r.userId))];
 for (const userId of userIds) {
 if (!userDataCache[userId]) {
 loadUserData(userId);
 }
 }
 }
 );

 return () => unsubProcessed();
 }, [showProcessed, selectedFranchiseId, selectedBranchId]);

 const loadUserData = async (userId: string) => {
 try {
 const userDoc = await getDoc(doc(db, 'users', userId));
 if (userDoc.exists()) {
 const userData = userDoc.data();
 setUserDataCache(prev => ({
 ...prev,
 [userId]: {
 displayName: userData?.displayName || 'Usuario',
 email: userData?.email || '',
 queuePoints: userData?.queuePoints || 0
 }
 }));
 }
 } catch (error) {
 console.error('Error loading user data:', error);
 }
 };

 const handleApprove = async (requestId: string) => {
 setProcessingId(requestId);

 try {
 const approveFn = httpsCallable(functions, 'approveConversion');
 await approveFn({ requestId });

 toast.success(t('admin.points.conversionApproved'));
 } catch (error: any) {
 console.error('Error approving conversion:', error);
 toast.error(error.message || t('admin.points.errorApprovingConversion'));
 } finally {
 setProcessingId(null);
 }
 };

 const handleReject = async (requestId: string, reason: string) => {
 if (!reason.trim()) {
 toast.error(t('admin.points.reasonRequired'));
 return;
 }

 setProcessingId(requestId);

 try {
 const rejectFn = httpsCallable(functions, 'rejectConversion');
 await rejectFn({
 requestId,
 reason
 });

 toast.success(t('admin.points.conversionRejected'));
 setRejectModalOpen(false);
 setRejectionReason('');
 setSelectedRequestId(null);
 } catch (error: any) {
 console.error('Error rejecting conversion:', error);
 toast.error(error.message || t('admin.points.errorRejectingConversion'));
 } finally {
 setProcessingId(null);
 }
 };

 const openRejectModal = (requestId: string) => {
 setSelectedRequestId(requestId);
 setRejectModalOpen(true);
 setRejectionReason('');
 };

 const handleRejectSubmit = () => {
 if (selectedRequestId) {
 handleReject(selectedRequestId, rejectionReason);
 }
 };

 return (
 <div className="space-y-6">
 {/* Pending Requests */}
 <div className="bg-white rounded-lg shadow-md bg-surface-2">
 <div className="border-b border-hairline p-4 bg-accent/10">
 <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
 <ArrowRightLeft className="w-5 h-5 text-accent" />
 {t('admin.points.pendingConversionRequests')}
 {pendingRequests.length > 0 && (
 <span className="ml-2 px-2 py-1 bg-accent text-on-accent text-xs font-bold rounded-full">
 {pendingRequests.length}
 </span>
 )}
 </h3>
 <p className="text-sm text-ink-muted mt-1">
 {t('admin.points.clientsWantConvert')}
 </p>
 </div>

 <div className="p-4">
 {loading ? (
 <div className="text-center py-8">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent/30 mx-auto"></div>
 </div>
 ) : pendingRequests.length === 0 ? (
 <div className="text-center py-8 text-ink-subtle">
 <Sparkles className="w-12 h-12 mx-auto mb-2 opacity-50" />
 <p className="text-sm">{t('admin.points.noPendingRequests')}</p>
 </div>
 ) : (
 <div className="space-y-4">
 {pendingRequests.map((request) => {
 const userData = userDataCache[request.userId];
 return (
 <div
 key={request.id}
 className="border-2 border-accent/30 rounded-lg p-4 bg-accent/10/50 bg-accent/10"
 >
 {/* User info */}
 <div className="flex justify-between items-start mb-4">
 <div className="flex-1">
 <p className="font-semibold text-ink text-lg">
 {userData?.displayName || t('common.loading')}
 </p>
 <p className="text-sm text-ink-muted">
 {userData?.email || ''}
 </p>
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted flex items-center gap-1">
 <Clock className="w-3 h-3" />
 {t('admin.points.requested')}: {new Date(request.createdAt?.toDate()).toLocaleString('es-ES')}
 </p>
 </div>
 <div className="text-right">
 <p className="text-xs text-ink-subtle">{t('admin.points.currentPoints')}</p>
 <p className="text-2xl font-bold text-accent">
 {userData?.queuePoints || 0}
 </p>
 </div>
 </div>

 {/* Conversion details */}
 <div className="bg-surface rounded-lg p-4 mb-4 border border-hairline">
 <div className="grid grid-cols-3 gap-3 text-center">
 <div>
 <p className="text-xs text-ink-muted">{t('admin.points.wantsToSpend')}</p>
 <p className="text-xl font-bold text-danger">
 -{request.pointsCost}
 </p>
 <p className="text-xs text-ink-subtle">{t('loyalty.points')}</p>
 </div>
 <div className="flex items-center justify-center">
 <ArrowRightLeft className="w-5 h-5 text-accent" />
 </div>
 <div>
 <p className="text-xs text-ink-muted">{t('admin.points.toObtain')}</p>
 <p className="text-xl font-bold text-success">
 +{request.stampsRequested}
 </p>
 <p className="text-xs text-ink-subtle">{t('loyalty.stamps')}</p>
 </div>
 </div>
 <div className="mt-3 pt-3 border-t border-hairline">
 <div className="flex justify-between items-center text-sm">
 <span className="text-ink-muted">{t('admin.points.remainingPoints')}:</span>
 <span className="font-bold text-ink">
 {(userData?.queuePoints || 0) - request.pointsCost}
 </span>
 </div>
 </div>
 </div>

 {/* Actions */}
 <div className="flex gap-2">
 <button
 onClick={() => openRejectModal(request.requestId)}
 disabled={processingId === request.id}
 className="flex-1 py-2 px-4 border border-danger/30 text-danger bg-surface rounded-lg hover:bg-danger/20 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
 >
 <X className="w-4 h-4" />
 {t('common.reject')}
 </button>
 <button
 onClick={() => handleApprove(request.requestId)}
 disabled={processingId === request.id}
 className="flex-1 py-2 px-4 bg-accent text-on-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-semibold"
 >
 <Check className="w-4 h-4" />
 {processingId === request.id ? t('common.processing') : t('admin.points.approveConversion')}
 </button>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 </div>

 {/* Toggle processed requests */}
 <div className="flex justify-center">
 <button
 onClick={() => setShowProcessed(!showProcessed)}
 className="text-sm text-accent hover:text-accent underline"
 >
 {showProcessed ? t('common.hide') : t('common.view')} {t('admin.points.processedRequests').toLowerCase()}
 </button>
 </div>

 {/* Processed Requests */}
 {showProcessed && (
 <div className="bg-white rounded-lg shadow-md bg-surface-2">
 <div className="border-b border-hairline p-4 bg-surface-2">
 <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
 <Clock className="w-5 h-5 text-ink-muted" />
 {t('admin.points.processedRequests')}
 {processedRequests.length > 0 && (
 <span className="ml-2 px-2 py-1 bg-surface-3 text-ink text-xs font-bold rounded-full">
 {processedRequests.length}
 </span>
 )}
 </h3>
 </div>

 <div className="p-4">
 {processedRequests.length === 0 ? (
 <div className="text-center py-8 text-ink-subtle">
 <p className="text-sm">{t('admin.points.noProcessedRequests')}</p>
 </div>
 ) : (
 <div className="space-y-3">
 {processedRequests.map((request) => {
 const userData = userDataCache[request.userId];
 const isApproved = request.status === 'approved';
 return (
 <div
 key={request.id}
 className={`border rounded-lg p-3 ${
 isApproved
 ? 'border-success/30 bg-success/10/30 bg-success/10'
 : 'border-danger/30 bg-danger/10/30 bg-danger/10'
 }`}
 >
 <div className="flex justify-between items-start">
 <div className="flex-1">
 <p className="font-semibold text-ink flex items-center gap-2">
 {isApproved ? (
 <Check className="w-4 h-4 text-success" />
 ) : (
 <X className="w-4 h-4 text-danger" />
 )}
 {userData?.displayName || t('common.loading')}
 </p>
 <p className="text-xs text-ink-subtle mt-1">
 {request.pointsCost} puntos → {request.stampsRequested} sellos
 </p>
 {!isApproved && request.rejectionReason && (
 <p className="text-xs text-danger mt-1 italic">
 {t('admin.points.reason')}: {request.rejectionReason}
 </p>
 )}
 </div>
 <div className="text-right">
 <span
 className={`text-xs font-bold px-2 py-1 rounded ${
 isApproved
 ? 'bg-success/10 text-success'
 : 'bg-danger/10 text-danger'
 }`}
 >
 {isApproved ? t('common.approved') : t('common.rejected')}
 </span>
 <p className="text-xs text-ink-subtle mt-1">
 {new Date(
 (isApproved ? request.approvedAt : request.rejectedAt)?.toDate()
 ).toLocaleDateString('es-ES')}
 </p>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 </div>
 )}

 {/* Reject Modal */}
 {rejectModalOpen && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-surface rounded-lg shadow-xl max-w-md w-full p-6">
 <h3 className="text-lg font-semibold text-ink mb-4">
 {t('admin.points.rejectConversion')}
 </h3>
 <p className="text-sm text-ink-muted mb-4">
 {t('admin.points.provideRejectionReason')}
 </p>
 <textarea
 value={rejectionReason}
 onChange={(e) => setRejectionReason(e.target.value)}
 placeholder={t('admin.points.rejectionPlaceholder')}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-danger focus:border-transparent resize-none"
 rows={4}
 autoFocus
 />
 <div className="flex gap-3 mt-6">
 <button
 onClick={() => {
 setRejectModalOpen(false);
 setRejectionReason('');
 setSelectedRequestId(null);
 }}
 className="flex-1 py-2 px-4 border border-hairline text-ink-muted bg-surface rounded-lg hover:bg-surface-3 font-medium"
 >
 {t('common.cancel')}
 </button>
 <button
 onClick={handleRejectSubmit}
 disabled={!rejectionReason.trim() || processingId !== null}
 className="flex-1 py-2 px-4 bg-danger text-white rounded-lg hover:bg-danger/90 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
 >
 {processingId ? t('admin.points.rejecting') : t('admin.points.confirmReject')}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};
